import axios from 'axios';

/**
 * Fetches city and state details for a given postal code and country.
 * Uses a multi-strategy lookup for global support and country auto-detection.
 * 
 * @param {string} pincode - The postal/zip code.
 * @param {string} countryCode - The ISO 2-letter country code (e.g., 'IN', 'US').
 * @returns {Promise<{success: boolean, city: string, state: string, country: string, countryCode: string}>}
 */
export const fetchPincodeDetails = async (pincode, countryCode = '') => {
    if (!pincode) return { success: false };

    // 1. Normalize the pincode
    // Keep alphanumeric for UK/Canada, but remove spaces/hyphens for general API use
    const rawPincode = pincode.trim();
    const cleanPincode = rawPincode.replace(/[\s-]/g, '').toUpperCase();
    const isAlpha = /[A-Z]/.test(cleanPincode);
    const isNumeric = /^\d+$/.test(cleanPincode);

    // 2. Define strategies based on patterns
    const strategies = [];

    // Strategy: Try the explicitly selected country first
    if (countryCode) {
        strategies.push({ code: countryCode, zip: cleanPincode });
        // For UK/Canada, also try truncated outward code if full fails
        if (isAlpha && cleanPincode.length > 4) {
            strategies.push({ code: countryCode, zip: cleanPincode.slice(0, 4) });
            strategies.push({ code: countryCode, zip: cleanPincode.slice(0, 3) });
        }
    }

    // Strategy: Auto-fallback based on length/format if selected country fails or none provided
    if (isNumeric) {
        if (cleanPincode.length === 6) strategies.push({ code: 'IN', zip: cleanPincode }, { code: 'SG', zip: cleanPincode });
        if (cleanPincode.length === 5) strategies.push({ code: 'US', zip: cleanPincode }, { code: 'DE', zip: cleanPincode }, { code: 'FR', zip: cleanPincode });
        if (cleanPincode.length === 4) strategies.push({ code: 'AU', zip: cleanPincode }, { code: 'ZA', zip: cleanPincode });
        if (cleanPincode.length >= 7) strategies.push({ code: 'JP', zip: cleanPincode }, { code: 'BR', zip: cleanPincode });
    } else if (isAlpha) {
        strategies.push({ code: 'GB', zip: cleanPincode.slice(0, 4) });
        strategies.push({ code: 'CA', zip: cleanPincode.slice(0, 3) });
    }

    // Remove duplicates from strategies while preserving order
    const uniqueStrategies = strategies.filter((v, i, a) => a.findIndex(t => t.code === v.code && t.zip === v.zip) === i);

    // 3. Execute lookups
    for (const s of uniqueStrategies) {
        try {
            // Specialized lookup for India (better coverage)
            if (s.code === 'IN' && s.zip.length === 6) {
                const res = await axios.get(`https://api.postalpincode.in/pincode/${s.zip}`);
                if (res.data?.[0]?.Status === 'Success') {
                    const data = res.data[0].PostOffice[0];
                    return {
                        success: true,
                        city: data.District,
                        state: data.State,
                        country: 'India',
                        countryCode: 'IN'
                    };
                }
            }

            // Global lookup via Zippopotam.us
            const zippoRes = await axios.get(`https://api.zippopotam.us/${s.code}/${s.zip}`);
            if (zippoRes.data?.places?.length > 0) {
                const place = zippoRes.data.places[0];
                return {
                    success: true,
                    city: place['place name'],
                    state: place['state'],
                    country: zippoRes.data['country'],
                    countryCode: zippoRes.data['country abbreviation']
                };
            }
        } catch (e) {
            // Continue to next strategy if this one fails
            continue;
        }
    }

    return { success: false };
};
